import * as THREE from 'three';
import type { PremiumCharacterInstance } from '../engine/entities/quaternius-characters.js';
import { gridCellToWorld } from '../floor/floor-grid.js';
import type { FloorLayoutModule } from '../floor/dynamic-floor.js';
import { findSalonPath } from '../navigation/salon-grid-nav.js';

type Phase =
  | 'client-enter'
  | 'client-walk'
  | 'client-turn'
  | 'client-sit'
  | 'client-wait'
  | 'waiter-walk'
  | 'waiter-serve'
  | 'done';

export class PilotServiceScenario {
  private phase: Phase = 'client-enter';
  private clientPath: THREE.Vector3[] = [];
  private waiterPath: THREE.Vector3[] = [];
  private pathIndex = 0;
  private turnTarget = 0;
  private timer = 0;
  private tableId = 3;
  private seatWorld = new THREE.Vector3();
  private waiterStand = new THREE.Vector3();

  constructor(
    private readonly client: PremiumCharacterInstance,
    private readonly waiter: PremiumCharacterInstance,
  ) {}

  reset(layout: FloorLayoutModule[]): void {
    this.phase = 'client-enter';
    this.pathIndex = 0;
    this.timer = 0;
    const table = layout.find((m) => m.logicalTableId === this.tableId && m.placed);
    const obstacles = layout
      .filter((m) => m.placed && m.sector === 'salon')
      .map((m) => ({ col: m.gridCol, row: m.gridRow, cols: m.gridCols, rows: m.gridRows }));
    const entrance = gridCellToWorld('salon', 0, 6, 1, 1);
    const goalCell = table
      ? { col: table.gridCol, row: table.gridRow + table.gridRows }
      : { col: 2, row: 2 };
    const pathCells = findSalonPath({ col: 0, row: 6 }, goalCell, obstacles);
    this.clientPath = pathCells.map((c) => {
      const w = gridCellToWorld('salon', c.col, c.row, 1, 1);
      return new THREE.Vector3(w.worldX, 0, w.worldZ);
    });
    if (this.clientPath.length === 0) {
      this.clientPath = [new THREE.Vector3(entrance.worldX, 0, entrance.worldZ)];
    }
    if (table) {
      this.seatWorld.set(table.worldX, 0, table.worldZ + 0.85);
      this.waiterStand.set(table.worldX - 0.9, 0, table.worldZ);
    }
    this.client.root.position.copy(this.clientPath[0] ?? new THREE.Vector3(entrance.worldX, 0, entrance.worldZ));
    this.waiter.root.position.set(-14, 0, -2);
    this.client.fsm.playState('idle');
    this.waiter.fsm.playState('idle');
    this.phase = 'client-walk';
    this.client.fsm.playState('walk');
  }

  isDone(): boolean {
    return this.phase === 'done';
  }

  update(dt: number): void {
    this.client.fsm.update(dt);
    this.waiter.fsm.update(dt);
    this.timer += dt;

    if (this.phase === 'client-walk') {
      this.moveAlongPath(this.client, this.clientPath, 'walk', dt, () => {
        this.phase = 'client-turn';
        this.turnTarget = Math.atan2(
          this.seatWorld.x - this.client.root.position.x,
          this.seatWorld.z - this.client.root.position.z,
        );
        this.client.fsm.playState('turn');
        this.timer = 0;
      });
      return;
    }

    if (this.phase === 'client-turn') {
      this.rotateToward(this.client.root, this.turnTarget, dt, () => {
        this.phase = 'client-sit';
        this.client.root.position.copy(this.seatWorld);
        this.client.fsm.playState('sit');
        this.timer = 0;
        window.setTimeout(() => this.client.fsm.playSitIdle(), this.client.fsm.clipDuration('sit') * 1000 * 0.92);
      });
      return;
    }

    if (this.phase === 'client-sit') {
      if (this.timer > this.client.fsm.clipDuration('sit') + 0.35) {
        this.phase = 'client-wait';
        this.client.fsm.playState('wait');
        this.waiterPath = [this.waiter.root.position.clone(), this.waiterStand.clone()];
        this.pathIndex = 0;
        this.phase = 'waiter-walk';
        this.waiter.fsm.playState('walk');
      }
      return;
    }

    if (this.phase === 'waiter-walk') {
      this.moveAlongPath(this.waiter, this.waiterPath, 'walk', dt, () => {
        this.phase = 'waiter-serve';
        this.waiter.fsm.playState('serveTable');
        this.timer = 0;
      });
      return;
    }

    if (this.phase === 'waiter-serve') {
      const face = Math.atan2(
        this.seatWorld.x - this.waiter.root.position.x,
        this.seatWorld.z - this.waiter.root.position.z,
      );
      this.rotateToward(this.waiter.root, face, dt, () => {});
      if (this.timer > this.waiter.fsm.clipDuration('serveTable') + 0.2) {
        this.waiter.fsm.playState('idle');
        this.phase = 'done';
      }
    }
  }

  private moveAlongPath(
    actor: PremiumCharacterInstance,
    path: THREE.Vector3[],
    moveState: 'walk',
    dt: number,
    onDone: () => void,
  ): void {
    if (path.length === 0) {
      onDone();
      return;
    }
    const target = path[this.pathIndex] ?? path[path.length - 1];
    const pos = actor.root.position;
    const dx = target.x - pos.x;
    const dz = target.z - pos.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 0.18) {
      this.pathIndex += 1;
      if (this.pathIndex >= path.length) {
        this.pathIndex = 0;
        actor.fsm.playState('idle');
        onDone();
        return;
      }
    } else {
      const speed = 1.35;
      pos.x += (dx / dist) * speed * dt;
      pos.z += (dz / dist) * speed * dt;
      actor.root.rotation.y = Math.atan2(dx, dz);
      if (actor.fsm.getState() !== moveState) actor.fsm.playState(moveState);
    }
  }

  private rotateToward(obj: THREE.Object3D, targetY: number, dt: number, onDone: () => void): void {
    const cur = obj.rotation.y;
    let delta = targetY - cur;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    if (Math.abs(delta) < 0.05) {
      obj.rotation.y = targetY;
      onDone();
      return;
    }
    obj.rotation.y += delta * Math.min(1, dt * 6);
  }
}
